import { inject, Injectable } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlag, NewPermission } from '@core/enums';
import { PermissionUtils } from '@core/utils/permission.utils';
import { Store } from '@ngxs/store';
import { SavedViewAddComponent } from '@shared/modules/saved-views/modals/saved-view-add/saved-view-add.component';
import { AccountState } from '@state/account/state';
import { SavedViewsState } from '@state/saved-view/state';
import { UserState } from '@state/user/state';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { CreateTaskListComponent } from '@tasks/task-shell/create-task-list/create-task-list.component';
import { TaskRelatedToModule } from '@tasks/tasks.types';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { filter, take } from 'rxjs';
import { TsPageSidenavConfig } from 'tsui/lib/ts-page/ts-page.component';

@Injectable()
export class TaskSidebarConfigService {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private matDialog = inject(MatLegacyDialog);
  private store = inject(Store);

  constructor() {}

  getConfig(relatedToType: TaskRelatedToModule): TsPageSidenavConfig {
    let actions = [];
    let canCreate = false;
    let canEdit = false;

    const isNewTaskPermissionsOn = this.store.selectSnapshot(
      AccountState.isFeatureEnabled(FeatureFlag.NEW_TASK_PERMISSIONS),
    );
    const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;
    const isGlobal = relatedToType === TaskRelatedToModule.Account;

    if (!isGlobal) {
      canCreate = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE));
      canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
    }

    if (isGlobal) {
      canCreate = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE));
      canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_EDIT));
    }

    if (isNewTaskPermissionsOn) {
      const createPermission = isAggregatedTasks
        ? NewPermission.AGGREGATED_TASK_LISTS_CREATE
        : NewPermission.GLOBAL_TASK_LISTS_CREATE;

      const currentTaskView = this.store.selectSnapshot(TaskState.getTaskView);
      canCreate = this.store.selectSnapshot(UserState.hasPermission(createPermission));
      canEdit = PermissionUtils.canEdit(currentTaskView);
    }

    if (canCreate) {
      actions.push({
        icon: 'add_circle_outline',
        description: 'Create new',
        actionFn: () => this.openCreateTaskListDialog(relatedToType),
      });
    }

    if (canEdit && !isAggregatedTasks) {
      actions.push({
        icon: 'ts_cog',
        description: 'Manage task lists',
        actionFn: () => this.navigateToSettings(relatedToType),
      });
    }

    return {
      title: '',
      actions,
    };
  }

  navigateToSettings(relatedToType: TaskRelatedToModule): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const segments = currentUrl.split('/').filter(Boolean);
    const idx = segments.lastIndexOf(relatedToType);
    if (idx < 0 || idx + 1 >= segments.length) {
      return;
    }
    const settingsUrl = '/' + segments.slice(0, idx + 2).join('/') + '/-1/settings';
    void this.router.navigateByUrl(settingsUrl);
  }

  openCreateTaskListDialog(relatedToType: TaskRelatedToModule) {
    const isAggregatedTasks = relatedToType === TaskRelatedToModule.AggregatedTasks;

    if (isAggregatedTasks) {
      const savedViewType = TaskRelatedToModule.AggregatedTasks;
      const dialogRef = this.matDialog.open(SavedViewAddComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          relatedToType: savedViewType,
          saved_views: this.store
            .selectSnapshot(SavedViewsState.getViews)
            .filter((x) => x.related_to_type === savedViewType),
        },
      });

      dialogRef
        .afterClosed()
        .pipe(
          filter((result) => !!result),
          take(1),
        )
        .subscribe(() => {
          this.store.dispatch(new TaskActions.GetSidebarItems());
        });
    } else {
      let applicableType = relatedToType as string;
      if (applicableType === TaskRelatedToModule.CustomObjectValue) {
        applicableType = this.store.selectSnapshot(JupiterState.getRelatedToObject).name;
      }

      this.matDialog.open(CreateTaskListComponent, { data: { applicableType } });
    }
  }
}
